import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

export interface CasePresenceUser {
	name: string;
	email: string;
	adReferenceId?: string;
	// Backend may send extra fields which we ignore.
	[key: string]: unknown;
}

export type CasePresenceGetTokens = () => Promise<{ appToken: string | undefined }>;

interface EnsureStartedInput {
	baseUrlOrBaseApiUrl: string;
	getTokens: CasePresenceGetTokens;
}

type ActiveUsersChangedHandler = (users: Array<Pick<CasePresenceUser, 'name' | 'email' | 'adReferenceId'>>) => void;

interface ActiveUsersChangedPayload {
	caseId?: string;
	users: Array<Pick<CasePresenceUser, 'name' | 'email' | 'adReferenceId'>>;
}

type ActiveUsersChangedScopedHandler = (payload: ActiveUsersChangedPayload) => void;

type UnsubscribeFn = () => void;

const HUB_PATH = '/hubs/casePresence';

const normalizeBaseUrl = (baseUrl: string) => baseUrl.replace(/\/+$/, '');

const coerceUsers = (users: unknown): Array<Pick<CasePresenceUser, 'name' | 'email' | 'adReferenceId'>> => {
	if (!Array.isArray(users)) return [];

	return users
		.map(u => (typeof u === 'object' && u !== null ? (u as Record<string, unknown>) : undefined))
		.filter((u): u is Record<string, unknown> => Boolean(u))
		.filter(u => typeof u.name === 'string' && typeof u.email === 'string')
		.map(u => ({
			name: u.name as string,
			email: u.email as string,
			adReferenceId: typeof u.adReferenceId === 'string' ? u.adReferenceId : undefined,
		}));
};

const coercePayload = (value: unknown): ActiveUsersChangedPayload => {
	if (Array.isArray(value)) {
		return { users: coerceUsers(value) };
	}

	if (typeof value === 'object' && value !== null) {
		const payload = value as Record<string, unknown>;
		const usersSource = Array.isArray(payload.activeUsers) ? payload.activeUsers : payload.users;
		return {
			caseId: typeof payload.caseId === 'string' ? payload.caseId : undefined,
			users: coerceUsers(usersSource),
		};
	}

	return { users: [] };
};

let connection: HubConnection | undefined;
let connectionBaseUrl: string | undefined;
let getTokensRef: CasePresenceGetTokens | undefined;
let startInFlight: Promise<void> | undefined;
let lastJoinedCaseId: string | undefined;

const buildConnection = (baseUrlOrBaseApiUrl: string, getTokens: CasePresenceGetTokens): HubConnection => {
	const hubUrl = `${normalizeBaseUrl(baseUrlOrBaseApiUrl)}${HUB_PATH}`;

	const conn = new HubConnectionBuilder()
		.configureLogging(LogLevel.None)
		.withUrl(hubUrl, {
			// withCredentials must be false when the backend uses AllowAnyOrigin() (Access-Control-Allow-Origin: *).
			// Browsers block credentialed responses paired with a wildcard origin.
			// Auth is handled via the bearer token from accessTokenFactory, not cookies.
			withCredentials: false,
			accessTokenFactory: async () => {
				try {
					const { appToken } = await getTokens();
					return appToken ?? '';
				} catch {
					return '';
				}
			},
		})
		.withAutomaticReconnect()
		.build();

	conn.onreconnected(async () => {
		if (!lastJoinedCaseId) return;
		try {
			await conn.invoke('JoinCase', lastJoinedCaseId);
		} catch {
			// Ignore; next heartbeat/join attempt can restore.
		}
	});

	return conn;
};

const ensureConnection = ({ baseUrlOrBaseApiUrl, getTokens }: EnsureStartedInput) => {
	if (connection && connectionBaseUrl === baseUrlOrBaseApiUrl) return;

	connection = buildConnection(baseUrlOrBaseApiUrl, getTokens);
	connectionBaseUrl = baseUrlOrBaseApiUrl;
	getTokensRef = getTokens;
};

const ensureStarted = async (input: EnsureStartedInput): Promise<void> => {
	ensureConnection(input);
	if (!connection) return;

	if (connection.state === HubConnectionState.Connected) return;
	if (startInFlight) return startInFlight;

	startInFlight = connection
		.start()
		.finally(() => {
			startInFlight = undefined;
		});

	return startInFlight;
};

const joinCase = async (caseId: string): Promise<void> => {
	if (!connection || !connectionBaseUrl || !getTokensRef) {
		throw new Error('casePresenceClient: ensureStarted must be called before joinCase');
	}

	await ensureStarted({ baseUrlOrBaseApiUrl: connectionBaseUrl, getTokens: getTokensRef });
	lastJoinedCaseId = caseId;
	await connection.invoke('JoinCase', caseId);
};

const heartbeat = async (caseId: string, signal?: AbortSignal): Promise<void> => {
	if (!connection || !connectionBaseUrl || !getTokensRef) {
		throw new Error('casePresenceClient: ensureStarted must be called before heartbeat');
	}

	signal?.throwIfAborted();
	await ensureStarted({ baseUrlOrBaseApiUrl: connectionBaseUrl, getTokens: getTokensRef });
	signal?.throwIfAborted();
	await connection.invoke('Heartbeat', caseId);
};

const onActiveUsersChanged = (handler: ActiveUsersChangedHandler): UnsubscribeFn => {
	if (!connection) {
		throw new Error('casePresenceClient: ensureStarted must be called before subscribing');
	}

	const wrapped = (users: unknown) => handler(coerceUsers(users));
	connection.on('activeUsersChanged', wrapped);
	return () => connection?.off('activeUsersChanged', wrapped);
};

const onActiveUsersChangedScoped = (handler: ActiveUsersChangedScopedHandler): UnsubscribeFn => {
	if (!connection) {
		throw new Error('casePresenceClient: ensureStarted must be called before subscribing');
	}

	const wrapped = (payload: unknown) => handler(coercePayload(payload));
	connection.on('activeUsersChanged', wrapped);
	return () => connection?.off('activeUsersChanged', wrapped);
};

const clearCurrentCase = (): void => {
	lastJoinedCaseId = undefined;
};

const stop = async (): Promise<void> => {
	if (!connection) return;

	lastJoinedCaseId = undefined;
	startInFlight = undefined;
	await connection.stop();
};

export const casePresenceClient = {
	ensureStarted,
	joinCase,
	heartbeat,
	onActiveUsersChanged,
	onActiveUsersChangedScoped,
	clearCurrentCase,
	stop,
};
